import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Image, 
  Layers, 
  User, 
  LogOut, 
  Menu, 
  X,
  Plus,
  BookOpen,
  ShoppingBag
} from 'lucide-react';
import { getApiUrl, makeRequest } from '../../utils/api';
import '../../styles/admin.css';

const AdminDashboard = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    portfolioCount: 0,
    servicesCount: 0
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const apiUrl = getApiUrl();
        const [portfolioRes, servicesRes] = await Promise.all([
          makeRequest('GET', `${apiUrl}/api/portfolio`),
          makeRequest('GET', `${apiUrl}/api/services`)
        ]);
        
        setStats({
          portfolioCount: portfolioRes.ok ? portfolioRes.data.length : 0,
          servicesCount: servicesRes.ok ? servicesRes.data.length : 0
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const seedData = async () => {
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/api/seed-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      alert(data.message);
      window.location.reload();
    } catch (error) {
      alert('Error seeding data');
    }
  };

  return (
    <div className="admin-layout">
      {/* Mobile Menu Toggle */}
      <button 
        className="admin-mobile-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <h2>The Slay Station</h2>
          <p>Admin Panel</p>
        </div>

        <nav className="admin-nav">
          <Link to="/admin/dashboard" className="admin-nav-item active">
            <LayoutDashboard size={20} />
            <span>Dashboard</span>
          </Link>
          <Link to="/admin/portfolio" className="admin-nav-item">
            <Image size={20} />
            <span>Portfolio</span>
          </Link>
          <Link to="/admin/services" className="admin-nav-item">
            <Layers size={20} />
            <span>Services</span>
          </Link>
          <Link to="/admin/storefront" className="admin-nav-item">
            <ShoppingBag size={20} />
            <span>Curated Favorites</span>
          </Link>
          <Link to="/admin/business" className="admin-nav-item">
            <User size={20} />
            <span>Bio & Info</span>
          </Link>
        </nav>

        <div className="admin-sidebar-footer">
          <Link to="/" className="admin-nav-item" target="_blank">
            <BookOpen size={20} />
            <span>View Site</span>
          </Link>
          <button onClick={handleLogout} className="admin-nav-item logout">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <header className="admin-header">
          <h1>Dashboard</h1>
          <p>Welcome back, {user?.email}</p>
        </header>

        <div className="admin-content">
          {/* Stats Cards */}
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <div className="admin-stat-icon">
                <Image size={32} />
              </div>
              <div className="admin-stat-info">
                <h3>{stats.portfolioCount}</h3>
                <p>Portfolio Items</p>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-icon">
                <Layers size={32} />
              </div>
              <div className="admin-stat-info">
                <h3>{stats.servicesCount}</h3>
                <p>Services</p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="admin-section">
            <h2>Quick Actions</h2>
            <div className="admin-actions-grid">
              <Link to="/admin/portfolio" className="admin-action-card">
                <Plus size={24} />
                <span>Add Portfolio Item</span>
              </Link>
              <Link to="/admin/services" className="admin-action-card">
                <Plus size={24} />
                <span>Add Service</span>
              </Link>
              <Link to="/admin/storefront" className="admin-action-card">
                <ShoppingBag size={24} />
                <span>Manage Curated Favorites</span>
              </Link>
              <Link to="/admin/business" className="admin-action-card">
                <User size={24} />
                <span>Edit Bio & Info</span>
              </Link>
            </div>
          </div>

          {/* Seed Data Button (only if no data) */}
          {stats.servicesCount === 0 && (
            <div className="admin-section">
              <h2>Setup</h2>
              <p style={{ marginBottom: '16px', color: '#666' }}>
                Import your existing site data to get started quickly.
              </p>
              <button onClick={seedData} className="admin-btn-primary">
                Import Existing Data
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
